import { MinsterAddressComponent } from "../ui-components/minster-address/minster-address.component";
import { MinsterSelectComponent } from "../ui-components/minster-select/minster-select.component";
import { ControlContainer, FormsModule, NgForm, NgModelGroup, FormGroup } from '@angular/forms';
import { MinsterInputComponent } from '../ui-components/minster-input/minster-input.component';
import { MinsterCardComponent } from '../ui-components/minster-card/minster-card.component';
import { MinsterDobComponent } from "../ui-components/minster-dob/minster-dob.component";
import { MinsterTelComponent } from '../ui-components/minster-tel/minster-tel.component';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { ScreenBaseComponent } from '../screen-base/screen-base.component';
import { InputType, ScreenType } from '../../models/enums.models';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ClaimsService } from '../../services/claims.service';
import { StateService } from '../../services/state.service';
import { MatFormField } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { Customer } from '../../models/customer.model';
import { MatDialog } from '@angular/material/dialog';
import { Claimant, Address } from '../../models/claim.model';
import { LookUp } from '../../models/look-up.model';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Component({
    selector: 'app-personal',
    standalone: true,
    imports: [MatCardModule, MinsterSelectComponent, MatButtonModule, FormsModule, MinsterInputComponent, MatExpansionModule,
        MatCheckboxModule, MatSelectModule, MinsterTelComponent, MinsterCardComponent,
        MatFormField, MinsterDobComponent, MatIconModule, MatInputModule, MinsterAddressComponent],
    templateUrl: './personal.component.html',
    styleUrl: './personal.component.css',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class PersonalComponent extends ScreenBaseComponent {
    @ViewChild(NgModelGroup) _formGroup: NgModelGroup | undefined;

    _titles: LookUp[];
    _telTypes: LookUp[];
    _inputTypes = InputType;
    _claimant: Claimant;
    _customer: Customer;
    _caseRef: string;

    readonly dialog = inject(MatDialog);
    _readonly: boolean = false;

    constructor(stateService: StateService, router: Router, toastr: ToastrService, claimService: ClaimsService
    ) {
        super(router, ScreenType.Personal, toastr, stateService, claimService);

        this._readonly = stateService.formIsSubmitted;
        this._titles = stateService.lookUps?.lookUpDictionary['Title']!;
        this._telTypes = stateService.lookUps?.lookUpDictionary['TelephoneNumberType']!;
        this._claimant = stateService.claim!.claimant!;
        this._customer = stateService.customer!;
        this._caseRef = stateService.claim!.claimReference;
    }

    //If the form is submitted, include 'required' errors, else ignore them
    override onNext(): void {
         if(this._readonly){
            super.onNext();
            return;
        }
        if (this._formGroup && this._formGroup.invalid) {
            let hasErrors = false;

            Object.keys(this._formGroup.control.controls).forEach((key) => {
                const control = this._formGroup!.control.get(key);

                if (control) {
                    const errors = control.errors;

                    if (errors) {
                        const hasOnlyRequiredError = Object.keys(errors).length === 1 && errors['required'];
                        if (this.stateService.formIsSubmitted) {
                            hasErrors = true;
                        }
                        else if (!hasOnlyRequiredError) {
                            hasErrors = true;
                        }
                    }

                    control.markAsTouched();
                    control.markAsDirty();
                    control.updateValueAndValidity();
                }
            });

            const phoneControls = [
                this._formGroup.control.get(this._fcn.Personal.PrimaryPhoneNoMobile),
                this._formGroup.control.get(this._fcn.Personal.SecondaryPhoneNo)
            ];

            phoneControls.forEach(control => {
                if (control && control.invalid) {
                    hasErrors = true;
                    control.markAsTouched();
                    control.markAsDirty();
                }
            });

            const personalAddressGroup = this._formGroup.control.get(this._formNames.PersonalAddress) as FormGroup;

            if (personalAddressGroup) {
                Object.keys(personalAddressGroup.controls).forEach((key) => {
                    const addressControl = personalAddressGroup.get(key);
                    if (addressControl && addressControl.invalid) {
                        const errors = addressControl.errors;

                        if (errors) {
                            const hasOnlyRequiredError = Object.keys(errors).length === 1 && errors['required'];
                            if (this.stateService.formIsSubmitted) {
                                hasErrors = true;
                            }
                            else if (!hasOnlyRequiredError) {
                                hasErrors = true;
                            }
                        }

                        addressControl.markAsTouched();
                        addressControl.markAsDirty();
                        addressControl.updateValueAndValidity();
                    }
                });
            }

            if (hasErrors) {
                this.toastr.warning('Please fix the form errors before proceeding.');
                this.stateService.enableSaving = false;
                return;
            }
        }

        //Check if the form is submitted and still invalid
        if (this.stateService.formIsSubmitted && this._formGroup!.invalid) {
            this.toastr.warning('Please fix the required fields before proceeding.');
            this.stateService.enableSaving = false;
            return;
        }

        this.stateService.enableSaving = this.hasStateChanged();

        super.onNext();
    }

    onAddPhoneNumber(): void {
        if (this._customer.customerContactDetails.length > 1) {
            this.toastr.warning('You can only add one additional phone number.');
            return;
        }

        if (this._customer.customerContactDetails.length === 1) {
            const newContactDetails = [...this._customer.customerContactDetails];

            newContactDetails.push({
                contactDetailId: 0,
                customerId: this._customer.customerId,
                telephoneNumberTypeId: 2,
                telephoneNumber: '',
                isPrimary: false,
                updatedBy: ''
            });

            const newContact = newContactDetails[1];
            newContact.isPrimary = newContact.telephoneNumberTypeId === 1 ? false : true;
            this._customer.customerContactDetails = newContactDetails;
        }
    }

    onRemoveAdditionalPhoneNumber(): void {
        if (this._customer.customerContactDetails.length > 1) {
            const newContactDetails = [...this._customer.customerContactDetails];
            newContactDetails.splice(1, 1);
            this._customer.customerContactDetails = newContactDetails;
        }
    }

    onPhoneTypeChange(index: number): void {
        if (this._customer.customerContactDetails[index]) {
            const contactDetail = this._customer.customerContactDetails[index];
            contactDetail.isPrimary = contactDetail.telephoneNumberTypeId === 1 ? false : true;
        }
    }

    get canAddPhone(): boolean {
        const contactDetails = this._customer.customerContactDetails ?? [];
        return contactDetails.length < 2 && this.isPhoneNumberValid(contactDetails[0]?.telephoneNumber);
    }

    private isPhoneNumberValid(phoneNumber: string): boolean {
        const phoneRegex = /^07[0-9]{8,13}$/;
        return phoneRegex.test(phoneNumber) && phoneNumber?.trim().length > 0;
    }

    //Section to keep both customer and claimant objects matched at all times
    onTitleChange(titleId: number) {
        this._claimant.titleId = titleId;
    }

    onForenameChange(forename: string) {
        this._claimant.forename = forename;
    }

    onSurnameChange(surname: string) {
        this._claimant.surname = surname;
    }

    onMiddleNameChange(middleName: string) {
        this._claimant.middleName = middleName;
    }

    onDobChange(dob: string) {
        this._claimant.dateOfBirth = dob;
    }

    onAddressChange(address: Address): void {
        this._claimant.address = {
            typeId: address.typeId,
            companyName: address.companyName,
            address1: address.address1,
            address2: address.address2,
            address3: address.address3,
            city: address.city,
            county: address.county,
            country: address.country,
            postcode: address.postcode,
            addressId: this._claimant.address?.addressId || 0 
        };
    }
}
